/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef } from 'react'
import { Decimal } from 'decimal.js'
import { InputNumber, Space } from 'antd'
import { AntdFormItem, AntdFormItemProps } from '@/common/components/AntdForm/AntdFormItem.tsx'
import { cn } from '@/utils/style.ts'
import { toDisplayNum } from '@/utils/misc.ts'

type CustomNumberInputProps = React.ComponentProps<typeof InputNumber> & {
  noSeparator?: boolean
  percent?: boolean
  maximumFractionDigits?: number
  addonBefore?: React.ReactNode
  addonAfter?: React.ReactNode
}

type AntdFormInputNumberProps = CustomNumberInputProps & AntdFormItemProps

export const CustomNumberInput: React.FC<CustomNumberInputProps> = forwardRef(({
  noSeparator,
  className,
  percent = false,
  maximumFractionDigits = 2,
  addonBefore,
  addonAfter,
  ...props
}, ref) => {

  // number to display value
  const formatter = (value?: string | number) => {
    if (value == undefined || value === '') return ''
    const finalValue = percent ? new Decimal(value).mul(100) : new Decimal(value)
    return noSeparator ? finalValue.toString() : toDisplayNum(finalValue.toNumber(), maximumFractionDigits)
  }

  // display value to number
  const parser = (value?: string) => {
    const finalValue = value?.replace(/,/g, '') || ''
    if (finalValue == undefined || finalValue === '' || isNaN(finalValue as unknown as number)) return ''
    if (!percent) return finalValue
    return (new Decimal(finalValue).div(100)).toString()
  }

  return (
    <Space.Compact className="w-full">
      {addonBefore}
      <InputNumber
        {...props}
        ref={ref}
        className={cn('w-full', className)}
        formatter={formatter as any}
        parser={parser}
        step={percent ? 0.01 : props.step}
        onCopy={e => {
          e.clipboardData.setData('text/plain', parser(e.currentTarget.value) || '')
          e.preventDefault()
        }}
      />
      {percent ? <Space.Addon>%</Space.Addon> : addonAfter}
    </Space.Compact>
  )
})

export const AntdFormNumberInput: React.FC<AntdFormInputNumberProps> = forwardRef(({
  fieldProps,
  ...props
}, ref) => {

  return (
    <AntdFormItem {...fieldProps}>
      <CustomNumberInput {...props} ref={ref} />
    </AntdFormItem>
  )
})
